import React, { useEffect, useState } from "react";
import { useParams, Navigate } from "react-router-dom";
import { fetchKeywordInfo,trackPageView } from "../api/dynamoQuery";
import "../styles/register.css"; // ✅ Load local styles first
import "../styles/form-builder.css"; // ✅ Form field styles


const LandingPage = () => {
  const { keyword, tier } = useParams(); // ✅ Capture tier
  const [keywordInfo, setKeywordInfo] = useState(null);
  const [form, setForm] = useState(null);
  const [disabledFields, setDisabledFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recaptchaToken, setRecaptchaToken] = useState(null);

  const SITE_KEY = "6LfgNtkqAAAAALeo-d57n0smoL14mAJKhJN_fFyo"; // ✅ Your reCAPTCHA site key 6LfgNtkqAAAAALeo-d57n0smoL14mAJKhJN_fFyo
  
  const uuid = Math.floor(Math.random() * 10**15).toString(); // Generates a large random number


  useEffect(() => {
    if (keyword) {
      const tracking_id = tier ? `go2survey:${keyword}-${tier}` : `go2survey:${keyword}`;
      console.log("Tracking Page View:", tracking_id); // ✅ Debugging
      trackPageView(tracking_id);
    }
  }, [keyword, tier]);
  

  useEffect(() => {
    async function getKeywordData() {
      setLoading(true);
      try {
        const data = await fetchKeywordInfo(keyword);
        if (!data || !data.keywordInfo || !data.form) {
          console.error("Invalid keyword or missing form:", data);
          setLoading(false);
          return;
        }

        setKeywordInfo(data.keywordInfo);
        setForm(data.form);

        // ✅ Identify disabled fields
        const disabled = [];
        if (data.keywordInfo.mobile_number) {
          disabled.push("static_field_mobile"); // ✅ Disable mobile field if present
        }
        setDisabledFields(disabled);

        // ✅ Load merchant CSS dynamically
        if (data.form.css_url) {
          const formCss = document.createElement("link");
          formCss.rel = "stylesheet";
          formCss.href = `${data.form.css_url}?v=${Date.now()}`;
          document.head.appendChild(formCss);
        }

        // ✅ Inject responsive layout overrides AFTER merchant CSS
        // Merchant CSS often uses width:fit-content which breaks mobile
        const responsiveOverride = document.createElement("style");
        responsiveOverride.textContent = `
          #form-outer-container{width:100%!important;max-width:620px!important;margin:24px auto!important;padding:16px!important;display:block!important}
          #form-preview-container{width:100%!important;max-width:100%!important;border-radius:14px;box-shadow:0 8px 40px rgba(0,0,0,.10),0 2px 8px rgba(0,0,0,.04);overflow:hidden}
          @media(max-width:480px){
            #form-outer-container{padding:8px!important;margin:8px auto!important}
            #form-preview-container{padding:24px 18px!important;border-radius:10px}
            #preview-submit{width:100%!important;padding:15px 24px!important}
          }
        `;
        document.head.appendChild(responsiveOverride);

        // ✅ Load jQuery first
        const jQueryScript = document.createElement("script");
        jQueryScript.src =
          "https://ajax.googleapis.com/ajax/libs/jquery/3.4.1/jquery.min.js";
        jQueryScript.async = true;
        jQueryScript.onload = () => {
          console.log("✅ jQuery Loaded");

          // ✅ Load form-render.min.js AFTER jQuery
          const formBuilderScript = document.createElement("script");
          formBuilderScript.src = "/js/form-render.min.js";
          formBuilderScript.async = true;
          formBuilderScript.onload = () => {
            console.log("✅ Form Builder Loaded");

            // register.js handles rendering for both single-page and multi-page forms
            // Do NOT render here to avoid race condition where this 300ms timeout
            // re-renders the form after register.js has already processed it

            // ✅ Load register.js after form renders
            const registerScript = document.createElement("script");
            registerScript.src = "/js/register.js";
            registerScript.async = true;
            registerScript.onload = () => {
              console.log("✅ Register.js Loaded");

              if (window.init) {
                // Pass full form config for multi-page support
                // register.js handles both legacy (formData) and new (pages) formats
                window.init(
                  data.form, // ✅ Full form config (includes pages, progressBar settings)
                  disabled, // ✅ Disabled Fields
                  "https://tq8f7dzxgh.execute-api.ap-southeast-2.amazonaws.com/prod/prod-intouch-smartlinks-keywords", // ✅ Smartlinks Lambda
                  data.keywordInfo.form_id || keyword, // ✅ Form ID for cookie naming
                  data.keywordInfo // ✅ Keyword info for mobile field handling
                );
              } else {
                console.error("❌ register.js did not initialize properly!");
              }
            };
            document.body.appendChild(registerScript);
          };
          document.body.appendChild(formBuilderScript);
        };
        document.body.appendChild(jQueryScript);

        // ✅ Load reCAPTCHA
        // ✅ Load reCAPTCHA Enterprise
        const recaptchaScript = document.createElement("script");
        recaptchaScript.src = "https://www.google.com/recaptcha/enterprise.js?render="+SITE_KEY;
        recaptchaScript.async = true;
        recaptchaScript.defer = true;
        document.body.appendChild(recaptchaScript);
      } catch (error) {
        console.error("❌ Error fetching keyword data:", error);
      } finally {
        setLoading(false);
      }
    }

    getKeywordData();
  }, [keyword]);
  // Enterprise reCAPTCHA
  const handleRecaptcha = async (e) => {
    e.preventDefault(); // Prevent default form submission

    if (!window.grecaptcha || !window.grecaptcha.enterprise) {
        alert("❌ reCAPTCHA is not loaded yet. Please try again.");
        return;
    }

    try {
        console.log("✅ reCAPTCHA is ready, executing...");
        const token = await window.grecaptcha.enterprise.execute(SITE_KEY, { action: "submit" });

        if (!token) {
            console.error("❌ Failed to generate reCAPTCHA token");
            alert("reCAPTCHA verification failed. Please try again.");
            return;
        }

        console.log("✅ reCAPTCHA token received:", token);

        // ✅ Inject token into hidden field
        let recaptchaInput = document.getElementById("g-recaptcha-response");
        if (!recaptchaInput) {
            console.error("❌ Missing #g-recaptcha-response hidden input field!");
            alert("reCAPTCHA validation error. Please refresh and try again.");
            return;
        }

        recaptchaInput.value = token;

        // ✅ Ensure form submission happens AFTER token is set
        console.log("🚀 Triggering form submission...");
        document.getElementById("preview-submit").click();

    } catch (error) {
        console.error("❌ reCAPTCHA execution error:", error);
        alert("An error occurred with reCAPTCHA. Please refresh and try again.");
    }
};


  if (loading) return <p>Loading...</p>;
  if (!keywordInfo) return <Navigate to="/" />;
  if (!form) return <p>Form configuration not found.</p>;

  return (
    <div>
      {/* ✅ FORM WRAPS EVERYTHING INCLUDING SUBMISSION */}
      <form id="entry-form">
        <div id="form-outer-container">
          <div className="processing-overlay" style={{ display: "none" }}>
            <h2>Submitting...</h2>
            <div className="templateLoader templateLoaderLrg"></div>
          </div>
          <div id="form-background-image"></div>
          <div className="form-container" id="form-preview-container">
            <input type="hidden" name="keyword" value={keyword} />
            <input type="hidden" name="form_id" value={keywordInfo.form_id} />
            <input type="hidden" name="merchant_id" value={keywordInfo.merchant_id} />
            <input type="hidden" name="preference_centre_id" value={keywordInfo.pref_centre_id} />
            <input type="hidden" name="survey_id" value={keywordInfo.pref_centre_id} />
            <input type="hidden" name="uuid" value={uuid} />
            <input type="hidden" name="tier" value={tier || ""} />

            <input type="hidden" id="g-recaptcha-response" name="g-recaptcha-response" value={recaptchaToken} />  
            {/* ✅ Title is here */}
            {form.title ? (
              <h2 id="preview-title">{form.title}</h2>
            ) : (
              <h2 id="preview-title">Form</h2>
            )}
            {/* ✅ This is where the form is injected */}
            <div id="form-preview"></div>
            {/* ✅ Submit button handled by register.js */}
            <div id="button-container">
              {/* ✅ This is the ONLY submit button, inside the form */}
              <button id="preview-submit" type="submit" disabled={false} onClick={handleRecaptcha}>
                {form.submitText ? form.submitText : "Submit"}
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default LandingPage;
